import type { BrowserEvent } from '@evidia/shared-mobile';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import cs_CZ from 'antd/locale/cs_CZ';
import dayjs from 'dayjs';
import 'dayjs/locale/cs';
import React, { lazy, useEffect } from 'react';
import { Navigate, Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';
import InAppLayout from './components/Layouts/InAppLayout';
import OutAppLayout from './components/Layouts/OutAppLayout';
import { StaticModalProvider } from './components/StaticModalContext';
import System from './components/System';
import { ROUTES } from './const/routes';
import { useUserFeatureFlags } from './hooks/useUserFeatureFlags';
import LoginPage from './pages/LoginPage/LoginPage';
import { Search } from './pages/Saarch/Search';
import { useDeviceStore } from './stores/DeviceStore';
import { trpc, trpcClient } from './utils/trcp';
/*
import DebugPage from './components/DebugPage';
import ApplicationFormPage from './pages/ApplicationFormPage';
import CalendarPage from './pages/CalendarPage';
import ConfirmRegistrationCodePage from './pages/ConfirmRegistrationCode';
import ContactPage from './pages/ContactPage';
import DocPage from './pages/DocPage';
import ExamDetail from './pages/Exams/ExamDetail';
import ExamResultsPage from './pages/Exams/ExamResultsPage';
import ExamsPage from './pages/Exams/ExamsPage';
import ExportEvidenceOfTrainings from './pages/Export/ExportEvidenceOfTrainings/ExportEvidenceOfTrainings';
import ExamExportPage from './pages/Export/ExportExamPage';
import ExportInvoicePage from './pages/Export/ExportInvoicePage';
import ReportExportPage from './pages/Export/ExportReportPage';
import ExportSchoolBook from './pages/Export/ExportSchoolBook';
import GroupBlockedPage from './pages/GroupBlockedPage';
import HomePage from './pages/HomePage/HomePage';
import InterpreterPage from './pages/InterpreterPage/InterpreterPage';
import LoginFlowPage from './pages/LoginFlowPage';
import MorePage from './pages/MorePage/MorePage';
import NewStudentPage from './pages/NewStudentPage';
import RegisterPage from './pages/RegisterPage';
import ReportDetail from './pages/Report/ReportDetail';
import ReportingPage from './pages/ReportingPage';
import ResetPassowrdConfirmPage from './pages/ResetPasswordConfirmPage';
import ResetPassowrdPage from './pages/ResetPasswordPage';
import SchoolInviteConfirmPage from './pages/SchoolInviteConfirmPage';
import SettingsPage from './pages/SettingsPage';
import StudentPassPage from './pages/Student/StudentPassPage/StudentPassPage';
import StudentCardPage from './pages/StudentCardPage';
import SupportPage from './pages/SupportPage';
import TeachersPage from './pages/TeachersPage/TeachersPage';
import TierPage from './pages/TierPage/TierPage';
import TrainingPage from './pages/TrainingPage/TrainingPage';
import UserSettingsPage from './pages/UserSettingsPage';

const PAGES = {
	ApplicationFormPage,
	ConfirmRegistrationCodePage,
	ContactPage,
	DocPage,
	ExamDetail,
	ExamResultsPage,
	ExamsPage,
	ExportEvidenceOfTrainings,
	ExamExportPage,
	ExportInvoicePage,
	ReportExportPage,
	ExportSchoolBook,
	GroupBlockedPage,
	HomePage,
	InterpreterPage,
	LoginFlowPage,
	LoginPage,
	MorePage,
	NewStudentPage,
	RegisterPage,
	ReportDetail,
	ReportingPage,
	SchoolInviteConfirmPage,
	SettingsPage,
	StudentPassPage,
	StudentCardPage,
	SupportPage,
	TeachersPage,
	TierPage,
	TrainingPage,
	UserSettingsPage,
	DebugPage,
	ResetPassowrdPage,
	ResetPassowrdConfirmPage,
	CalendarPage,
};
*/
// Lazy loaded pages

const PAGES = {
	ApplicationFormPage: lazy(() => import('./pages/ApplicationFormPage')),
	ConfirmRegistrationCodePage: lazy(() => import('./pages/ConfirmRegistrationCode')),
	ContactPage: lazy(() => import('./pages/ContactPage')),
	DocPage: lazy(() => import('./pages/DocPage')),
	ExamDetail: lazy(() => import('./pages/Exams/ExamDetail')),
	ExamResultsPage: lazy(() => import('./pages/Exams/ExamResultsPage')),
	ExamsPage: lazy(() => import('./pages/Exams/ExamsPage')),
	ExportEvidenceOfTrainings: lazy(() => import('./pages/Export/ExportEvidenceOfTrainings/ExportEvidenceOfTrainings')),
	ExamExportPage: lazy(() => import('./pages/Export/ExportExamPage')),
	ExportInvoicePage: lazy(() => import('./pages/Export/ExportInvoicePage')),
	ReportExportPage: lazy(() => import('./pages/Export/ExportReportPage')),
	ExportSchoolBook: lazy(() => import('./pages/Export/ExportSchoolBook')),
	GroupBlockedPage: lazy(() => import('./pages/GroupBlockedPage')),
	HomePage: lazy(() => import('./pages/HomePage/HomePage')),
	InterpreterPage: lazy(() => import('./pages/InterpreterPage/InterpreterPage')),
	LoginFlowPage: lazy(() => import('./pages/LoginFlowPage')),
	LoginPage: lazy(() => import('./pages/LoginPage/LoginPage')),
	MorePage: lazy(() => import('./pages/MorePage/MorePage')),
	NewStudentPage: lazy(() => import('./pages/NewStudentPage')),
	RegisterPage: lazy(() => import('./pages/RegisterPage')),
	ReportDetail: lazy(() => import('./pages/Report/ReportDetail')),
	ReportingPage: lazy(() => import('./pages/ReportingPage')),
	ResetPasswordConfirmPage: lazy(() => import('./pages/ResetPasswordConfirmPage')),
	TeacherStatisticsPage: lazy(() => import('./pages/TeacherStatisticsPage')),
	ResetPasswordPage: lazy(() => import('./pages/ResetPasswordPage')),
	SchoolInviteConfirmPage: lazy(() => import('./pages/SchoolInviteConfirmPage')),
	SettingsPage: lazy(() => import('./pages/SettingsPage')),
	StudentPassPage: lazy(() => import('./pages/Student/StudentPassPage/StudentPassPage')),
	StudentCardPage: lazy(() => import('./pages/StudentCardPage')),
	SupportPage: lazy(() => import('./pages/SupportPage')),
	TeachersPage: lazy(() => import('./pages/TeachersPage/TeachersPage')),
	TierPage: lazy(() => import('./pages/TierPage/TierPage')),
	TrainingPage: lazy(() => import('./pages/TrainingPage/TrainingPage')),
	UserSettingsPage: lazy(() => import('./pages/UserSettingsPage')),
	DebugPage: lazy(() => import('./components/DebugPage')),
	ResetPassowrdPage: lazy(() => import('./pages/ResetPasswordPage')),
	ResetPassowrdConfirmPage: lazy(() => import('./pages/ResetPasswordConfirmPage')),
	CalendarPage: lazy(() => import('./pages/CalendarPage')),
};

const env: 'DEV' | 'PROD' = (import.meta.env.VITE_APP_ENV as any) ?? 'DEV';
const SENTRY_ENV: Record<'DEV' | 'PROD', string> = {
	DEV: 'development',
	PROD: 'production',
};

if (window.location.hostname !== 'localhost' && window.location.hostname !== 'dev-app-evidia.jale.cz') {
	Sentry.init({
		environment: SENTRY_ENV[env],

		dsn: 'https://d78d9dad22dd5d32f4f085defd1c46fb@o4508087077765120.ingest.de.sentry.io/4508087079927888',
		integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
		// Tracing
		tracesSampleRate: 1.0, //  Capture 100% of the transactions
		// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
		tracePropagationTargets: [
			'localhost',
			/^https:\/\/dev-app-evidia.jale\.cz\/trpc/,
			/^https:\/\/app.evidia\.cz\/trpc/,
		],
		// Session Replay
		// This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
		replaysSessionSampleRate: 0,
		// If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
		replaysOnErrorSampleRate: 1.0,
		tunnel: 'https://log.evidia.cz/log',
		sampleRate: 1.0,
		attachStacktrace: true,
	});
}
dayjs.locale('cs');

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
		},
	},
});

const App: React.FC = () => {
	const setNavigationOpened = useDeviceStore(state => state.setNavigationOpened);
	const featureFlags = useUserFeatureFlags();

	useEffect(() => {
		(window as any).fireBrowserEvent = (payload: BrowserEvent): Promise<void> => {
			if (payload.type === 'HideNavigation') {
				setNavigationOpened(!payload.hide);
				return Promise.resolve();
			}

			return Promise.resolve();
		};
	}, []);

	const router = createBrowserRouter(
		createRoutesFromElements(
			<>
				<Route
					element={
						<System>
							<InAppLayout />
						</System>
					}
					path="/app"
				>
					<Route path="" Component={PAGES.HomePage} />

					<Route path={ROUTES.APP.SUB_CALENDAR} Component={PAGES.CalendarPage} />

					<Route path={ROUTES.APP.SUB_NEW_STUDENT} Component={PAGES.NewStudentPage} />
					<Route path={ROUTES.APP.SUB_EXAMS} Component={PAGES.ExamsPage} />
					<Route path={`${ROUTES.APP.SUB_EXAMS_DETAIL}/:id`} Component={PAGES.ExamDetail} />
					<Route path={`${ROUTES.APP.SUB_EXAMS_RESULTS}/:id`} Component={PAGES.ExamResultsPage} />

					<Route path={ROUTES.APP.SUB_REPORTING} Component={PAGES.ReportingPage} />
					<Route path={`${ROUTES.APP.SUB_REPORTING_DETAIL}/:id`} Component={PAGES.ReportDetail} />
					<Route path={ROUTES.APP.SUB_TEACHER_STATISTICS} Component={PAGES.TeacherStatisticsPage} />
					<Route path={`${ROUTES.APP.SUB_SETTINGS}/:type`} Component={PAGES.SettingsPage} />
					<Route path={ROUTES.APP.SUB_SUPPORT} Component={PAGES.SupportPage} />
					<Route path={ROUTES.APP.SUB_TRAININGS} Component={PAGES.TrainingPage} />
					<Route path={`${ROUTES.APP.SUB_STUDENT}/:id`} Component={PAGES.StudentCardPage} />

					<Route path={`${ROUTES.APP.SUB_MORE}`} Component={PAGES.MorePage} />
					<Route path={ROUTES.APP.SUB_LOGIN_FLOW} Component={PAGES.LoginFlowPage} />
					<Route path={ROUTES.APP.SUB_GROUP_BLOCKED} Component={PAGES.GroupBlockedPage} />

					{featureFlags.DEBUG && <Route path={ROUTES.APP.SUB_DEBUG} Component={PAGES.DebugPage} />}
					<Route path={ROUTES.APP.SUB_TIER} Component={PAGES.TierPage} />
					<Route path={ROUTES.APP.SUB_TEACHERS} Component={PAGES.TeachersPage} />
					<Route path={ROUTES.APP.SUB_USER_SETTINGS} Component={PAGES.UserSettingsPage} />
					<Route path={ROUTES.APP.SUB_APPLICATION_FORM} Component={PAGES.ApplicationFormPage} />

					<Route path={`${ROUTES.APP.SUB_INTERPRETER}`} Component={PAGES.InterpreterPage} />

					<Route path={ROUTES.APP.SEARCH} Component={Search} />

					<Route path="*" element={<Navigate to={ROUTES.APP.ROOT} />} />
				</Route>
				<Route
					element={
						<System>
							<OutAppLayout />
						</System>
					}
					path="/"
				>
					<Route path="" Component={LoginPage} />

					<Route
						path={`${ROUTES.OUT_APP.STUDENT}/:schoolId/:registrationNumber`}
						Component={PAGES.StudentPassPage}
					/>

					<Route path={ROUTES.OUT_APP.SUB_REGISTER} Component={PAGES.RegisterPage} />
					<Route path={ROUTES.OUT_APP.SUB_LOGIN} Component={PAGES.LoginPage} />
					<Route path={ROUTES.OUT_APP.SUB_RESET_PASSWORD} Component={PAGES.ResetPassowrdPage} />
					<Route
						path={`${ROUTES.OUT_APP.SUB_RESET_PASSWORD_CONFIRM}/:code`}
						Component={PAGES.ResetPassowrdConfirmPage}
					/>
					<Route
						path={`${ROUTES.OUT_APP.SUB_SCHOOL_INVITE_CONFIRM}/:code`}
						Component={PAGES.SchoolInviteConfirmPage}
					/>
					<Route
						path={`${ROUTES.OUT_APP.SUB_CONFIR_REGISTRATION_CODE}/:code`}
						Component={PAGES.ConfirmRegistrationCodePage}
					/>
					<Route path={`${ROUTES.OUT_APP.DOC}`} Component={PAGES.DocPage} />
					<Route path={`${ROUTES.OUT_APP.CONTACT}`} Component={PAGES.ContactPage} />

					<Route path="*" element={<Navigate to={ROUTES.APP.ROOT} />} />
				</Route>
				<Route path="/print">
					<Route path="export-exam" Component={PAGES.ExamExportPage} />
					<Route path="export-report" Component={PAGES.ReportExportPage} />
					<Route path="export-invoice" Component={PAGES.ExportInvoicePage} />
					<Route path="export-evidence-of-trainings" Component={PAGES.ExportEvidenceOfTrainings} />
					<Route path="export-school-book" Component={PAGES.ExportSchoolBook} />
				</Route>
			</>,
		),
	);

	return (
		<ConfigProvider
			theme={{
				token: {
					colorPrimary: '#2875ea',
					colorSuccess: '#52c41a',
					colorWarning: '#faad14',
					colorError: '#ff4d4f',
					colorInfo: '#2875ea',
				},
			}}
			locale={cs_CZ}
		>
			<trpc.Provider client={trpcClient} queryClient={queryClient}>
				<QueryClientProvider client={queryClient}>
					{/* <BrowserRouter> */}
					{/* <System> */}
					<StaticModalProvider>
						<RouterProvider router={router} />
					</StaticModalProvider>
					{/* </System> */}
					{/* </BrowserRouter> */}
				</QueryClientProvider>
			</trpc.Provider>
		</ConfigProvider>
	);
};

export default App;
